import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { HOME_BY_ROLE, useAuth } from "../hooks/useAuth";
import { Button, Card, Field, SelectField } from "../components/ui";
import { TotokInfoPanel } from "../components/TotokInfo";
import { GENDER_OPTIONS } from "../components/gender";
import { api } from "../api/client";
import { ssoStatus } from "../api/auth";
import { useQuery } from "@tanstack/react-query";
import { HandHeart } from "lucide-react";
import type { Gender, TenantPublic } from "../api/types";

// ── Landing: pilih tenant ──
export function LandingPage() {
  const { isAuthed, homePath } = useAuth();
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.get<{ tenants: TenantPublic[] }>("/tenants"),
  });

  if (isAuthed) return <Navigate to={homePath} replace />;

  return (
    <div className="auth-shell">
      <div className="auth-split">
        <div className="auth-hero">
          <span className="brand-mark brand-mark-lg" aria-hidden>
            <HandHeart size={28} />
          </span>
          <h1>Reservasi Totok Punggung</h1>
          <p>Pilih lokasi, tentukan waktu, bayar mudah lewat QRIS.</p>
          <Link to="/login" className="btn btn-primary btn-lg">
            Masuk
          </Link>
          <Link to="/register" className="link">
            Belum punya akun? Daftar
          </Link>
        </div>
        <TotokInfoPanel />
      </div>

      <div className="container landing-list">
        <h2>Pilih lokasi layanan</h2>
        {tenantsQuery.isLoading ? (
          <div className="state-box">
            <span className="spinner" />
          </div>
        ) : tenantsQuery.isError ? (
          <p className="state-title">Gagal memuat lokasi. Silakan muat ulang halaman.</p>
        ) : (
          <div className="tenant-grid">
            {tenantsQuery.data?.tenants.map((t) => (
              <Link key={t.id} to={`/t/${t.slug}`} className="card tenant-card">
                <div className="tenant-card-head">
                  <span className="tenant-logo" aria-hidden>
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <h3>{t.name}</h3>
                    <p className="muted">{t.address}</p>
                  </div>
                </div>
                <p className="tenant-hours">
                  Buka {t.openingTime}–{t.closingTime}
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Login ──
const SSO_MESSAGES: Record<string, string> = {
  no_account:
    "Akun Zetri Anda belum tertaut ke akun Totok. Masuk dengan email dan kata sandi, lalu minta admin menautkannya.",
  email_unverified: "Email akun Zetri Anda belum terverifikasi sehingga belum bisa dipakai masuk.",
  inactive: "Akun Anda tidak aktif. Hubungi admin lokasi.",
  link_conflict: "Akun Zetri ini sudah tertaut ke pengguna Totok lain.",
  state_invalid: "Sesi login SSO kedaluwarsa. Silakan coba lagi.",
  disabled: "Login SSO belum diaktifkan.",
  access_denied: "Anda membatalkan proses masuk lewat Zetri SSO.",
};

export function LoginPage() {
  const { isAuthed, homePath, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const ssoReason = params.get("sso");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const sso = useQuery({ queryKey: ["sso", "status"], queryFn: ssoStatus, retry: false, staleTime: 300_000 });
  const ssoMessage = ssoReason
    ? (SSO_MESSAGES[ssoReason] ?? "Login lewat Zetri SSO gagal. Silakan coba lagi atau masuk dengan kata sandi.")
    : null;

  if (isAuthed) return <Navigate to={homePath} replace />;

  const from = (location.state as { from?: string } | null)?.from ?? homePath;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Terjadi kesalahan. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-split">
        <Card className="auth-card">
          <div className="auth-card-head">
            <span className="brand-mark" aria-hidden>
              <HandHeart size={18} />
            </span>
            <h1>Masuk</h1>
            <p className="muted">Lanjutkan ke akun Anda.</p>
          </div>
          {error ? <p className="alert alert-error">{error}</p> : null}
          {ssoMessage ? <p className="alert alert-error">{ssoMessage}</p> : null}
          <form onSubmit={onSubmit} className="form-stack">
            <Field
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field
              label="Kata sandi"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" loading={submitting} className="btn-block">
              Masuk
            </Button>
          </form>
          {sso.data?.enabled ? (
            <a className="btn btn-outline btn-block sso-login" href="/api/auth/sso/start">
              Masuk dengan Zetri SSO
            </a>
          ) : null}
          <p className="auth-foot">
            Belum punya akun? <Link to="/register">Daftar sebagai pasien</Link>
          </p>
        </Card>
        <TotokInfoPanel />
      </div>
    </div>
  );
}

// ── Register ──
export function RegisterPage() {
  const { isAuthed, homePath, register } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthed) return <Navigate to={homePath} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!gender) {
      setError("Jenis kelamin wajib dipilih.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await register({
        fullName,
        email,
        phone: phone || undefined,
        password,
        gender,
      });
      // Pendaftaran langsung membuat sesi → arahkan ke beranda peran yang bersangkutan.
      navigate(HOME_BY_ROLE[user.role], { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Terjadi kesalahan. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <div className="auth-card-head">
          <span className="brand-mark" aria-hidden>
            <HandHeart size={18} />
          </span>
          <h1>Daftar</h1>
          <p className="muted">Buat akun pasien Anda.</p>
        </div>
        {error ? <p className="alert alert-error">{error}</p> : null}
        <form onSubmit={onSubmit} className="form-stack">
          <Field
            label="Nama lengkap"
            name="fullName"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <SelectField
            label="Jenis kelamin"
            name="gender"
            required
            options={GENDER_OPTIONS}
            placeholder="Pilih jenis kelamin"
            value={gender}
            onChange={(e) => setGender(e.target.value as Gender | "")}
          />
          <p className="state-hint" style={{ margin: 0 }}>
            Dipakai sistem untuk memilih terapis dengan jenis kelamin yang sama.
          </p>
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label="Nomor HP (opsional)"
            name="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Field
            label="Kata sandi (min. 8 karakter)"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" loading={submitting} className="btn-block">
            Daftar
          </Button>
        </form>
        <p className="auth-foot">
          Sudah punya akun? <Link to="/login">Masuk</Link>
        </p>
      </Card>
    </div>
  );
}
